/*
 * App page tabs: pure model, no React and nothing that touches `window`. The tab names, their labels, which one is
 * active and the URL search each tab maps to. Archiving lives in the header's more menu, so every tab fits one row on
 * a phone; the Shadow mode tab returns with docs/mvp-plan.md P18.
 */
export const APP_TABS = ['overview', 'profiles', 'env', 'stores'] as const;
export type AppTab = (typeof APP_TABS)[number];

export const TAB_LABEL: Record<AppTab, string> = { overview: 'Overview', profiles: 'Profiles', env: 'Env requirements', stores: 'Stores' };

/** Shorter labels below `sm`, so the four tabs stay on one row down to 320 px (the full label stays the accessible name). */
export const TAB_SHORT_LABEL: Partial<Record<AppTab, string>> = { env: 'Env' };

/** The tab in the URL (`overview` when absent), or `overview` when it is not a tab of the page (an old `shadow` link). */
export function activeTab(requested: string | undefined): AppTab {
  return (APP_TABS as readonly string[]).includes(requested ?? '') ? (requested as AppTab) : 'overview';
}

/** The search of the app page URL for a tab: `overview` is the default and keeps the URL clean. */
export function tabSearch(tab: string): { tab?: AppTab } {
  return tab === 'overview' ? {} : { tab: tab as AppTab };
}

/**
 * The bundle id the header shows: the one its profiles check, else "Set in your app config" for an Expo app (the build
 * reads it from app.json / app.config), else nothing (no "no bundle id yet" noise).
 */
export function bundleIdLabel(bundleId: string | undefined, framework: string): { text: string; known: boolean } | undefined {
  if (bundleId) return { text: bundleId, known: true };
  return framework === 'expo' ? { text: 'Set in your app config', known: false } : undefined;
}

/** The line above the app.json snippet a client commits (SPEC decision 23: no provider or provider CLI named). */
export const CLIENT_APP_JSON_NOTE = 'Commit this in the app’s app.json (or app.config.*): the build checks the owner, the slug and the project id.';

/** A native app's build setup in a client's words: ready, or prepared by Hangar (the client has nothing to do; SPEC decision 30). */
export const nativeSetupNote = (ready: boolean): string =>
  ready ? 'Native builds of this app are set up: Run build starts them.' : 'Hangar prepares native builds for this app. Run build starts them once they are ready.';
